import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "featured" | "compact";
}

export default function ArticleCard({ article, variant = "default" }: ArticleCardProps) {
  const formattedDate = new Date(article.publishedAt).toLocaleDateString("zh-CN", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const formattedViews = article.viewCount >= 10000
    ? `${(article.viewCount / 10000).toFixed(1)}万`
    : article.viewCount.toLocaleString();

  if (variant === "featured") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group block bg-background-100 rounded-lg overflow-hidden transition-all duration-300 hover:ring-1 hover:ring-primary-500/30 cursor-pointer"
      >
        <div className="relative aspect-[16/9] overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            title={`${article.title} - 黑料社区`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent"></div>
          <div className="absolute top-3 left-3 flex gap-2 flex-wrap">
            {article.isExclusive && (
              <span className="px-2 py-0.5 bg-primary-500/90 text-background-950 text-xs font-medium rounded">
                独家
              </span>
            )}
            {article.isHot && (
              <span className="px-2 py-0.5 bg-accent-500/90 text-background-950 text-xs font-medium rounded flex items-center gap-1">
                <i className="ri-fire-line text-xs w-3 h-3 flex items-center justify-center"></i>
                热门
              </span>
            )}
          </div>
        </div>
        <div className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs text-primary-400 bg-primary-500/10 px-2 py-0.5 rounded">
              {article.category}
            </span>
            <span className="text-xs text-foreground-600">{formattedDate}</span>
          </div>
          <h3 className="font-heading font-semibold text-lg text-foreground-100 group-hover:text-primary-400 transition-colors mb-2 line-clamp-2">
            {article.title}
          </h3>
          <p className="text-foreground-500 text-sm leading-relaxed line-clamp-2 mb-4">
            {article.excerpt}
          </p>
          <div className="flex items-center justify-between text-xs text-foreground-600">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <i className="ri-eye-line w-3 h-3 flex items-center justify-center"></i>
                {formattedViews}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-chat-3-line w-3 h-3 flex items-center justify-center"></i>
                {article.commentCount}
              </span>
            </div>
            <span>{article.readTime} 分钟阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-4 p-3 bg-background-100 rounded-lg hover:ring-1 hover:ring-primary-500/20 transition-all duration-200 cursor-pointer"
      >
        <div className="w-24 h-16 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            title={`${article.title} - 黑料社区`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-heading font-medium text-sm text-foreground-200 group-hover:text-primary-400 transition-colors line-clamp-2 mb-1">
            {article.title}
          </h4>
          <div className="flex items-center gap-3 text-xs text-foreground-600">
            <span className="text-primary-400">{article.category}</span>
            <span>{formattedDate}</span>
            <span>{formattedViews} 阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  // Default variant
  return (
    <Link
      to={`/article/${article.id}`}
      className="group block bg-background-100 rounded-lg overflow-hidden transition-all duration-300 hover:ring-1 hover:ring-primary-500/20 cursor-pointer"
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          title={`${article.title} - 黑料社区`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-3 left-3 flex gap-2">
          {article.isHot && (
            <span className="px-2 py-0.5 bg-accent-500/90 text-background-950 text-xs font-medium rounded flex items-center gap-1">
              <i className="ri-fire-line text-xs w-3 h-3 flex items-center justify-center"></i>
              热门
            </span>
          )}
        </div>
      </div>
      <div className="p-4 md:p-5">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs text-primary-400 bg-primary-500/10 px-2 py-0.5 rounded">
            {article.category}
          </span>
          <span className="text-xs text-foreground-600">{article.readTime} 分钟阅读</span>
        </div>
        <h3 className="font-heading font-semibold text-base text-foreground-100 group-hover:text-primary-400 transition-colors mb-2 line-clamp-2">
          {article.title}
        </h3>
        <p className="text-foreground-500 text-sm leading-relaxed line-clamp-2 mb-3">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between text-xs text-foreground-600">
          <span>{formattedDate}</span>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line w-3 h-3 flex items-center justify-center"></i>
              {formattedViews}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line w-3 h-3 flex items-center justify-center"></i>
              {article.commentCount}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}